import { cn } from "@/lib/utils";

/**
 * A description's own markup, painted the way modsy paints it — on **both**
 * surfaces, the editor and the read side.
 *
 * **The donor's rendering is user-agent defaults, and that is the spec.** MUI's
 * `CssBaseline` resets exactly four things — `html`, `box-sizing`, `strong, b`,
 * and `body` (`CssBaseline.js:63-80`) — the donor theme's only override touches
 * `body` (`theme.ts:296-305`), and the editor never imports `Draft.css` (it
 * hand-rolls the one placeholder rule that file would have supplied,
 * `RichTextEditor.tsx:124-133`). So every heading, list, table and footnote
 * marker in a modsy description renders at the browser's own defaults wherever
 * it appears. Tailwind's preflight strips every one of them, which is what
 * these put back — in `em` where the UA uses `em`, rather than the nearest
 * comfortable Tailwind step.
 *
 * Shared rather than written twice because the two surfaces agreeing *is* the
 * requirement: the same description is edited and then read, and markup that
 * changes shape between the two is the bug. The one rule deliberately **not**
 * here is paragraph spacing — see `RICH_TEXT_READ_PAINT`.
 */
export const RICH_TEXT_PAINT = cn(
  // UA: `ol,ul { margin: 1em 0; padding-left: 40px }`.
  "[&_ol]:my-[1em] [&_ol]:list-decimal [&_ol]:pl-10",
  "[&_ul]:my-[1em] [&_ul]:list-disc [&_ul]:pl-10",
  // …but UA also flushes *nested* lists: `ul ul, ul ol, ol ul, ol ol` get no
  // block margin. Without this every level of a nested list gains 14px above
  // and below that modsy does not show.
  "[&_ol_ol]:my-0 [&_ol_ul]:my-0 [&_ul_ol]:my-0 [&_ul_ul]:my-0",
  // UA changes the bullet shape by depth — `ul ul, ol ul` are `circle`, the
  // third level `square`. Preflight kills `list-style` outright and a single
  // `list-disc` would restore discs all the way down.
  "[&_ol_ul]:list-[circle] [&_ul_ul]:list-[circle]",
  "[&_ul_ol_ul]:list-[square] [&_ul_ul_ul]:list-[square]",
  "[&_ol_ol_ul]:list-[square] [&_ol_ul_ul]:list-[square]",
  // Tables are allowlisted by the sanitiser (`rich_text.py`), so a migrated
  // description can carry one. Preflight collapses borders and zeroes all
  // padding, which butts cells against each other; UA is
  // `border-collapse: separate; border-spacing: 2px` with `1px` cells.
  "[&_table]:border-separate [&_table]:border-spacing-[2px]",
  "[&_td]:p-px [&_th]:p-px",
  // UA heading sizes and margins, all at `font-weight: bold`.
  "[&_h1]:my-[0.67em] [&_h1]:[font-size:2em] [&_h1]:font-bold",
  "[&_h2]:my-[0.83em] [&_h2]:[font-size:1.5em] [&_h2]:font-bold",
  "[&_h3]:my-[1em] [&_h3]:[font-size:1.17em] [&_h3]:font-bold",
  "[&_h4]:my-[1.33em] [&_h4]:[font-size:1em] [&_h4]:font-bold",
  // h5/h6 carry `!`: `index.css` ends with an *unlayered* global
  // `h5, h6 { font-size: 14px; margin: 0 }`, and an unlayered declaration beats
  // every `@layer utilities` rule whatever its specificity — so without the
  // important flag these two silently render as body copy. Scoped here rather
  // than by changing that global, which is app-wide typography.
  "[&_h5]:my-[1.67em]! [&_h5]:[font-size:0.83em]! [&_h5]:font-bold",
  "[&_h6]:my-[2.33em]! [&_h6]:[font-size:0.67em]! [&_h6]:font-bold",
  // Preflight rewrites `sub`/`sup` to `75%` on a zeroed line box with a
  // relative offset; the UA uses `smaller` with its own baseline shift. Both
  // tags are allowlisted and the spec names them, so a migrated superscript
  // should not come out 10% small at the wrong height.
  //
  // The leading is `inherit`, not `normal`: the root's `leading-[1.5]` is a
  // unitless number, so inheriting it lets each tag compute 1.5 against its own
  // smaller font, which is what the donor does. `normal` looks like the same fix
  // and is not — it shortens every line carrying a footnote marker by about half
  // a line.
  "[&_sub]:static [&_sub]:[vertical-align:sub] [&_sub]:[font-size:smaller] [&_sub]:[line-height:inherit]",
  "[&_sup]:static [&_sup]:[vertical-align:super] [&_sup]:[font-size:smaller] [&_sup]:[line-height:inherit]",
  // `strong, b` is one of CssBaseline's four resets, and the donor theme sets no
  // `fontWeightBold`, so it is MUI's default 700 — not 600.
  "[&_b]:font-bold [&_em]:italic [&_i]:italic [&_strong]:font-bold",
  // **Links are the browser's own blue, not the house navy.** Nothing in the
  // donor styles an anchor: no rule in `RichTextEditor.tsx`, a bare `<a>` out of
  // the decorator (`draftJsUtils.tsx:20-27`), and an unstyled read side. `revert`
  // says exactly that — roll the property back to the user-agent origin. More
  // honest than naming a hex, and more correct: it restores `:link` *and*
  // `:visited` in each browser's own colours, and it keeps a raw colour literal
  // out of a codebase whose colours live in `index.css` as tokens. (`:visited`
  // could not have been tested anyway — browsers deliberately lie to
  // `getComputedStyle` about it.)
  "[&_a]:[color:revert] [&_a]:[text-decoration-line:revert]",
);

/**
 * Paragraph spacing, which belongs to the **read side only**.
 *
 * It looks like an omission from `RICH_TEXT_PAINT` and is not. Draft-js renders
 * an unstyled block as a bare `<div>` (`DefaultDraftBlockRenderMap`), so a
 * paragraph in the donor's *editor* has no margin at all; `stateToHTML` then
 * writes `<p>` on export, which the read side renders at the UA's `1em 0`. The
 * same description is therefore flush while you edit it and spaced once you save
 * it — in modsy. Atlas types `<p>` directly (`defaultParagraphSeparator`), so it
 * reproduces that only by leaving this rule off the editor.
 */
export const RICH_TEXT_READ_PAINT = "[&_p]:my-[1em]";
